/**
 * PSV-1911 — virtual joystick for touch play.
 *
 * Left 40% of the screen is the movement zone: touch-down plants the
 * joystick base under the finger, dragging past the deadzone thrusts the
 * ship toward the drag direction (the vector feeds the existing analog
 * `aim` on the ShipInput heartbeat — no new netcode). Release stops.
 *
 * Everything right of the zone stays the fire surface — the controller's
 * existing scene-level `pointerdown` already fires toward the tap's
 * world point, so aimed shots on touch come for free. This class only
 * has to (a) own its pointer so joystick drags never discharge shots and
 * (b) report thrust/aim for the input heartbeat.
 *
 * Pure input+render helper: no network, no game state. The controller
 * polls `thrust()`/`aim()` from its readKeyboard pass.
 */

import Phaser from 'phaser';

/** Movement zone: leftmost fraction of the screen. */
const ZONE_FRACTION = 0.4;
/** Max thumb travel from the base (px, internal 1280x720 coords). */
const RADIUS = 72;
/** Drag magnitude (0..1) below which the stick reads as neutral. */
const DEADZONE = 0.22;
const DEPTH = 30;
const COLOR = 0x35c4f0;

export class TouchControls {
  private readonly scene: Phaser.Scene;
  private base: Phaser.GameObjects.Graphics | null = null;
  private thumb: Phaser.GameObjects.Graphics | null = null;
  /** Phaser pointer id currently driving the stick; -1 when idle. */
  private pointerId = -1;
  private originX = 0;
  private originY = 0;
  private dx = 0;
  private dy = 0;
  private destroyed = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.base = scene.add.graphics().setScrollFactor(0).setDepth(DEPTH).setVisible(false);
    this.base.lineStyle(2, COLOR, 0.4);
    this.base.strokeCircle(0, 0, RADIUS);
    this.base.fillStyle(COLOR, 0.06);
    this.base.fillCircle(0, 0, RADIUS);
    this.thumb = scene.add.graphics().setScrollFactor(0).setDepth(DEPTH + 1).setVisible(false);
    this.thumb.fillStyle(COLOR, 0.35);
    this.thumb.fillCircle(0, 0, 26);

    scene.input.on('pointerdown', this.handleDown, this);
    scene.input.on('pointermove', this.handleMove, this);
    scene.input.on('pointerup', this.handleUp, this);
    scene.input.on('pointerupoutside', this.handleUp, this);
  }

  /** True while a finger is driving the stick. */
  isActive(): boolean {
    return this.pointerId !== -1;
  }

  /** Binary thrust for the ShipInput heartbeat (analog isn't in the contract). */
  thrust(): -1 | 0 | 1 {
    if (!this.isActive()) return 0;
    return this.magnitude() >= DEADZONE ? 1 : 0;
  }

  /** World heading of the drag vector (screen == world orientation). */
  aim(): number | undefined {
    if (!this.isActive() || this.magnitude() < DEADZONE) return undefined;
    return Math.atan2(this.dy, this.dx);
  }

  /**
   * Fire-guard for the controller: joystick-zone touches (and the live
   * joystick pointer wherever it wanders) must never discharge a shot.
   */
  swallowsFire(pointer: { x: number; id?: number }): boolean {
    if (typeof pointer.id === 'number' && pointer.id === this.pointerId) return true;
    return pointer.x < this.scene.cameras.main.width * ZONE_FRACTION;
  }

  destroy(): void {
    this.destroyed = true;
    this.scene.input.off('pointerdown', this.handleDown, this);
    this.scene.input.off('pointermove', this.handleMove, this);
    this.scene.input.off('pointerup', this.handleUp, this);
    this.scene.input.off('pointerupoutside', this.handleUp, this);
    this.base?.destroy();
    this.base = null;
    this.thumb?.destroy();
    this.thumb = null;
    this.pointerId = -1;
  }

  // ---------- private ----------

  private magnitude(): number {
    return Math.min(1, Math.hypot(this.dx, this.dy) / RADIUS);
  }

  private handleDown(pointer: Phaser.Input.Pointer): void {
    if (this.destroyed || this.pointerId !== -1) return;
    if (pointer.x >= this.scene.cameras.main.width * ZONE_FRACTION) return;
    this.pointerId = pointer.id;
    this.originX = pointer.x;
    this.originY = pointer.y;
    this.dx = 0;
    this.dy = 0;
    this.base?.setPosition(pointer.x, pointer.y).setVisible(true);
    this.thumb?.setPosition(pointer.x, pointer.y).setVisible(true);
  }

  private handleMove(pointer: Phaser.Input.Pointer): void {
    if (this.destroyed || pointer.id !== this.pointerId) return;
    const rawX = pointer.x - this.originX;
    const rawY = pointer.y - this.originY;
    const len = Math.hypot(rawX, rawY);
    const clamp = len > RADIUS ? RADIUS / len : 1;
    this.dx = rawX * clamp;
    this.dy = rawY * clamp;
    this.thumb?.setPosition(this.originX + this.dx, this.originY + this.dy);
  }

  private handleUp(pointer: Phaser.Input.Pointer): void {
    if (this.destroyed || pointer.id !== this.pointerId) return;
    this.pointerId = -1;
    this.dx = 0;
    this.dy = 0;
    this.base?.setVisible(false);
    this.thumb?.setVisible(false);
  }
}
